/* AMYO: snapshot current FT settings before the existing cart checkout submits. */
(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const bypass = new WeakSet();
  let pending = false;

  function shipDate(days, today = new Date()) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
    for (let added = 0; added < days;) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) added++;
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  function desiredProperties(item, today) {
    const properties = { ...item.properties };
    delete properties._fulfillment_type;
    delete properties._fulfillment_days;
    delete properties._fulfillment_ship_date;
    if (item.type) properties._fulfillment_type = item.type;
    if (item.type.includes('instock') && !item.type.includes('mto') && !item.type.includes('preorder')) {
      const days = Number(item.days);
      if (Number.isSafeInteger(days) && days > 0 && days <= 365) {
        properties._fulfillment_days = String(days);
        // Preserve the cart's existing four-business-day minimum for this group.
        properties._fulfillment_ship_date = shipDate(Math.max(4, days), today);
      }
    }
    return properties;
  }

  function equalProperties(a, b) {
    const entries = value => Object.entries(value || {}).filter(([, v]) => v != null && v !== '').sort(([x], [y]) => x.localeCompare(y));
    return JSON.stringify(entries(a)) === JSON.stringify(entries(b));
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        if (response.status === 429 && attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          continue;
        }
        if (!response.ok) throw new Error(`FT cart request failed (${response.status})`);
        return await response.text();
      }
    } finally { clearTimeout(timeout); }
  }

  function parseSnapshot(html) {
    const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-amyo-ft-order-data]');
    if (!data) throw new Error('FT cart data is unavailable');
    const items = JSON.parse(data.textContent);
    if (!Array.isArray(items) || items.some(item => typeof item.key !== 'string' || typeof item.type !== 'string' || !Number.isInteger(item.quantity) || item.quantity < 1)) {
      throw new Error('FT cart data is invalid');
    }
    // Liquid serializes line properties as [name, value] pairs; Ajax uses an object.
    for (const item of items) {
      if (Array.isArray(item.properties)) item.properties = Object.fromEntries(item.properties);
    }
    return items;
  }

  async function readSnapshot() {
    return parseSnapshot(await request(`${root}cart?section_id=amyo-ft-order-data&_ft=${Date.now()}`));
  }

  async function synchronize() {
    const today = new Date();
    let items = await readSnapshot();
    const maxUpdates = items.length * 3 + 5;
    for (let updates = 0; updates < maxUpdates; updates++) {
      const item = items.find(line => !equalProperties(line.properties, desiredProperties(line, today)));
      if (!item) return;
      const properties = desiredProperties(item, today);
      // Shopify cannot replace existing properties with an empty object. Empty
      // values remove our old fields without touching engraving/gift properties.
      for (const key of ['_fulfillment_type', '_fulfillment_days', '_fulfillment_ship_date']) {
        if (!(key in properties) && item.properties?.[key]) properties[key] = '';
      }
      const response = JSON.parse(await request(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties, sections: ['amyo-ft-order-data'], sections_url: `${root}cart` })
      }));
      // Property edits change Shopify line keys and may merge identical lines.
      // Re-read before each edit so quantities and unrelated properties survive.
      const section = response.sections?.['amyo-ft-order-data'];
      items = section ? parseSnapshot(section) : await readSnapshot();
    }
    throw new Error('Cart changed while refreshing shipping estimates');
  }

  async function refreshThenContinue(button, continueCheckout) {
    if (pending) return;
    pending = true;
    button.setAttribute('aria-busy', 'true');
    button.parentElement?.querySelector('[data-amyo-ft-error]')?.remove();
    try {
      await synchronize();
      button.removeAttribute('aria-busy');
      continueCheckout();
    } catch (error) {
      console.warn('AMYO shipping estimate refresh failed:', error);
      const message = document.createElement('p');
      message.dataset.amyoFtError = '';
      message.setAttribute('role', 'alert');
      message.textContent = 'We couldn’t refresh your shipping estimate. Please try checkout again.';
      const refresh = document.createElement('a');
      refresh.href = `${root}cart`;
      refresh.textContent = ' Refresh cart';
      message.append(refresh);
      button.insertAdjacentElement('beforebegin', message);
    } finally {
      pending = false;
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || new URL(form.action, location.href).pathname.replace(/\/$/, '') !== `${root}cart`.replace(/\/$/, '')) return;
    const button = event.submitter;
    if (!button || button.name !== 'checkout') return;
    if (bypass.has(form)) { bypass.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    refreshThenContinue(button, () => {
      // Property edits can merge lines; do not post stale positional quantities.
      form.querySelectorAll('[name="updates[]"]').forEach(input => { input.disabled = true; });
      bypass.add(form);
      form.requestSubmit(button);
    });
  }, true);

  // Redo replaces the normal cart button with two controls in an open shadow
  // root. Refresh before either handler adds/removes coverage and redirects.
  document.addEventListener('click', event => {
    const button = event.composedPath().find(node => node instanceof HTMLButtonElement &&
      node.matches('[data-target="coverage-button"], [data-target="non-coverage-button"]'));
    if (!button || button.getRootNode().host?.localName !== 'redo-shopify-toggle') return;
    if (bypass.has(button)) { bypass.delete(button); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    refreshThenContinue(button, () => {
      bypass.add(button);
      button.click();
    });
  }, true);
})();
